import { Component, ElementRef, HostListener, OnDestroy } from '@angular/core'
import { FormControl } from '@angular/forms'
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap'
import { DisplayDateFormat } from '@northfork/basics'
import { Subject, Subscription } from 'rxjs'
import {
  debounceTime,
  distinctUntilChanged,
  map,
  switchMap,
} from 'rxjs/operators'
import {
  HttpDialogListValuesService,
  ResolvedLists,
} from '../../services/http-dialog-list-values.service'
import {
  DerivationResult,
  TransactionDerivationSource,
} from '../../services/transaction-derivation.service'
import {
  derivationRequest,
  derivationTriggers,
  FieldDerivation,
} from '../../edit-transaction/derivation'
import {
  EditTransactionCollection,
  EditTransactionCollectionColumn,
} from '../../edit-transaction/edit-transaction.projection'
import {
  blankCellLog,
  normalizeOptions,
} from '../../edit-transaction/list-options'
import {
  editValue,
  formatFieldValue,
} from '../../edit-transaction/format-field-value'
import {
  ControlSource,
  controlFor,
  EditTransactionControl,
} from '../edit-transaction-modal/edit-transaction-grid'
import {
  pickerLabel,
  ValuePicker,
  ValuePickerOption,
} from '../value-picker/value-picker'

/** See the dialog's own constant. */
const LOOKUP_DEBOUNCE_MS = 300

/** One pending search: which cell asked, what it typed, and what the panel hangs off. */
interface ItemLookupRequest {
  row: CollectionItemRow
  column: EditTransactionCollectionColumn
  line: number
  key: string
  query: string
  anchor: HTMLElement
}

/** One editable line of a collection, keyed by column field. */
export type CollectionItemRow = Record<string, string>

/**
 * The hidden key a line carries the index of the ORIGINAL line it started as (PVAL-26440).
 *
 * Absent on a line added here. No column can be named this — a metadata field is a word, never a
 * pair of underscores — and the modal only ever reads the cells its columns name, so the key rides
 * along unseen through edits, adds and removals, and the dialog behind reads it back to know which
 * raw line (with keys no column shows) each line is.
 */
export const LINE_ORIGIN_KEY = '__lineOrigin'

/** The original-line index a row carries, or null for a line added in the dialog. */
export const lineOriginOf = (row: CollectionItemRow): number | null => {
  const origin = row[LINE_ORIGIN_KEY]
  if (origin == null || origin === '') return null
  const index = Number(origin)
  return Number.isInteger(index) && index >= 0 ? index : null
}

/** What the modal hands back when Done is pressed. Dismissal returns nothing and changes nothing. */
export interface CollectionItemsResult {
  name: string
  rows: CollectionItemRow[]
}

/**
 * Column widths by control, taken from the prototype's own `ITEM_W`.
 *
 * Sized per control rather than evenly: a date needs room for a picker and a currency does not, and
 * every column being equal is what forces the widest one to set them all and pushes the last off the
 * edge. These widths are why the whole line fits in one view without a horizontal scrollbar.
 */
const ITEM_WIDTH: Readonly<Record<EditTransactionControl, number>> = {
  enum: 148,
  lookup: 148,
  date: 122,
  number: 108,
  text: 128,
}

/**
 * Everything on a line that is not one of its columns, from the stylesheet that draws it:
 * `$col-index` + `$col-actions` for the two gutters, `.ci-row`'s `gap`, and `.ci-items`' own
 * horizontal padding. Read together by {@link CollectionItemsModalComponent.modalWidth}.
 */
const LINE_GUTTERS = 34 + 34
const LINE_GAP = 8
const BODY_PADDING = 16 * 2

/** Room for the body's own scrollbar, so the last column never sits under it. */
const SCROLLBAR_ALLOWANCE = 22

/**
 * The narrowest a modal is allowed to be.
 *
 * A two-column collection (Other Payments, Keywords) needs only ~400px by its columns' own seed
 * widths, which left its values tight — `PerShareCommission` and `LOAN_FACILITY_TIER` both brush
 * their cell's edge. So a short collection is stretched to this instead, and its COLUMNS grow with
 * it (see {@link CollectionItemsModalComponent.width}) rather than the extra width sitting empty
 * to the right of the lines. A wide collection is already past it and is untouched.
 *
 * Matches the floor in the stylesheet, which applies until the collection arrives.
 */
const MIN_MODAL_WIDTH = 480

/**
 * The item editor behind an Array Summary field (PVAL-26341).
 *
 * A collection — GL distribution lines, a trade's other payments — is summarised as a single row in
 * the dialog and edited here, in a modal over it. That mirrors the design's Array container: the
 * summary reconciles against the other sources, the lines themselves are only ever edited.
 *
 * Rows repeat rather than sit in a grid, following `create-rule.component` in
 * recon-configuration-ui: a header row of column labels and one line per item. A grid would bring
 * sorting, filtering and cell-editor lifecycles to a handful of lines that want none of them — and
 * lines are added and removed here far more often than they are scanned.
 *
 * Dumb, like the dialog that opens it: it is handed a projected collection and hands back rows. It
 * has no metadata, no services, and does not know which record it belongs to.
 */
@Component({
  selector: 'hs-collection-items-modal',
  templateUrl: './collection-items-modal.component.html',
  styleUrls: ['./collection-items-modal.component.scss'],
})
export class CollectionItemsModalComponent implements OnDestroy {
  /** Assigned by the host immediately after `NgbModal.open`. */
  collection!: EditTransactionCollection

  /** Working copy — the dialog's own rows are untouched until Done. */
  rows: CollectionItemRow[] = []

  /** True once anything has been typed, added or removed. Drives the footer's standing note. */
  dirty = false

  readonly picker = new ValuePicker()

  /** `MM/dd/yyyy` — the app-wide display format, handed to the date control as the dialog behind this does. */
  readonly dateDisplayFormat = DisplayDateFormat

  /**
   * A date cell's control, by line and then by column. Stable per cell: a new one on every change
   * detection would re-bind the picker's input each tick.
   */
  private readonly dateControls = new WeakMap<
    CollectionItemRow,
    Map<string, FormControl>
  >()

  private readonly dateBindings = new Subscription()

  /** The line and column the open panel belongs to, so a pick knows where to be written. */
  private pickerTarget: { row: CollectionItemRow; field: string } | null = null

  /** The account the dialog behind this was opened for — what every `/resolve` call names. */
  private accountId = ''

  /** Per-line derivation: trigger column -> the columns derived from it (PVAL-26685). */
  private triggers = new Map<string, string[]>()

  /** Each line's last committed cells, so a commit that changed nothing asks nothing. */
  private readonly committed = new WeakMap<
    CollectionItemRow,
    CollectionItemRow
  >()

  /** The newest request per line and column. An older reply for the same cell is dropped. */
  private readonly derivationSeq = new WeakMap<
    CollectionItemRow,
    Map<string, number>
  >()
  private nextSeq = 0

  private readonly derivationCalls = new Subscription()

  constructor(
    readonly activeModal: NgbActiveModal,
    private readonly host: ElementRef<HTMLElement>,
    private readonly listValues: HttpDialogListValuesService,
    private readonly derivations: TransactionDerivationSource,
  ) {
    this.lookups.add(
      this.typed
        .pipe(
          debounceTime(LOOKUP_DEBOUNCE_MS),
          distinctUntilChanged(
            (before, after) =>
              before.key === after.key && before.query === after.query,
          ),
          switchMap(request =>
            this.listValues
              .search(request.column.field, request.query, this.accountId)
              .pipe(map(result => ({ request, result }))),
          ),
        )
        .subscribe(({ request, result }) =>
          this.onLookupResult(request, result),
        ),
    )
  }

  // ── Lookups ──────────────────────────────────────────────────────────────────
  // The same control as the dialog behind this, for the same reason — a `search` column's set is
  // unbounded, so it stays a text box and offers what the typed text matched. Keyed by CELL
  // (column + line), because the same column is searched independently on every line.

  private readonly typed = new Subject<ItemLookupRequest>()

  private readonly lookups = new Subscription()

  /** The cell whose search is in flight, and the cell whose last search found nothing. */
  searchingKey: string | null = null
  noMatchesKey: string | null = null

  /** Options last offered per cell, so a cell can show the picked row's `text`. */
  private readonly lookupOptions = new Map<string, ValuePickerOption[]>()

  /** The modal's once-per-(field, column) log for rows missing a display column. */
  private readonly blankCells = blankCellLog()

  /** Whether this column searches at all. A `lookup` with no `search` list is free text. */
  searchable(column: EditTransactionCollectionColumn): boolean {
    return column.minChars != null
  }

  /**
   * What a cell's box shows (PVAL-26685): the `text` of the loaded option its value matches —
   * this cell's last search, else the column's own list — or the stored value itself.
   */
  lookupText(
    row: CollectionItemRow,
    column: EditTransactionCollectionColumn,
    line: number,
  ): string {
    const options =
      this.lookupOptions.get(this.pickerKey(column, line)) ??
      column.listValues ??
      []
    return pickerLabel(options, row[column.field])
  }

  /** A keystroke in a lookup cell. The text is stored first, search or no search. */
  onLookupInput(
    row: CollectionItemRow,
    column: EditTransactionCollectionColumn,
    line: number,
    text: string,
    anchor: HTMLElement,
  ): void {
    const key = this.pickerKey(column, line)
    this.onEdit(row, column.field, text)
    if (!this.searchable(column)) return

    const query = text.trim()
    this.noMatchesKey = null
    if (query.length < (column.minChars ?? 0)) {
      this.searchingKey = null
      if (this.picker.isOpen(key)) this.picker.close()
      return
    }
    this.searchingKey = key
    this.typed.next({ row, column, line, key, query, anchor })
  }

  private onLookupResult(
    request: ItemLookupRequest,
    result: ResolvedLists,
  ): void {
    this.searchingKey = null
    const failure = result.error ?? result.failures.get(request.column.field)
    if (failure != null) {
      console.error(
        `[transactions-2] lookup search failed for ${request.column.field}; the cell stays ` +
          'free text',
        failure,
      )
      this.noMatchesKey = null
      if (this.picker.isOpen(request.key)) this.picker.close()
      return
    }

    const rows = result.options.get(request.column.field) ?? []
    const options = request.column.listKeys
      ? normalizeOptions(
          rows,
          request.column.listKeys,
          this.blankCells(request.column.field),
        )
      : []
    if (!options.length) {
      this.noMatchesKey = request.key
      if (this.picker.isOpen(request.key)) this.picker.close()
      return
    }

    this.noMatchesKey = null
    this.lookupOptions.set(request.key, options)
    this.pickerTarget = { row: request.row, field: request.column.field }
    this.picker.show(
      request.key,
      options,
      request.anchor,
      this.host.nativeElement,
      request.column.displayColumns,
    )
  }

  ngOnDestroy(): void {
    // An open panel holds window scroll/resize listeners that only `close` removes; left attached they
    // would keep the panel, its anchor and this modal's element alive after the modal is gone.
    this.picker.close()
    this.lookups.unsubscribe()
    this.derivationCalls.unsubscribe()
    this.dateBindings.unsubscribe()
  }

  /** Dismissal for the value panel — see {@link ValuePicker.dismiss}. */
  @HostListener('document:mousedown', ['$event.target'])
  onDocumentMouseDown(target: EventTarget | null): void {
    this.picker.dismiss(target)
  }

  /** The panel's key for one cell. Public: the template keys its per-cell search state by it. */
  pickerKey(column: EditTransactionCollectionColumn, line: number): string {
    return column.field + '~' + line
  }

  private pickerOptionsFor(
    column: EditTransactionCollectionColumn,
  ): ValuePickerOption[] {
    return column.listValues ?? []
  }

  /** Whether this cell is the one whose panel is open. Drives the chevron's direction. */
  pickerOpen(column: EditTransactionCollectionColumn, line: number): boolean {
    return this.picker.isOpen(this.pickerKey(column, line))
  }

  /** What an enum cell reads at rest: the LABEL of the value it holds, not the value. */
  pickerText(
    row: CollectionItemRow,
    column: EditTransactionCollectionColumn,
  ): string {
    return pickerLabel(this.pickerOptionsFor(column), row[column.field])
  }

  /** Open this cell's panel, or shut it if it is the one already open. */
  openPicker(
    row: CollectionItemRow,
    column: EditTransactionCollectionColumn,
    line: number,
    anchor: HTMLElement,
    event: Event,
  ): void {
    event.preventDefault()
    this.picker.toggle(
      this.pickerKey(column, line),
      this.pickerOptionsFor(column),
      row[column.field],
      anchor,
      this.host.nativeElement,
      column.displayColumns,
    )
    this.pickerTarget = this.picker.open ? { row, field: column.field } : null
  }

  /** A value pressed in the panel, written through the same path a typed edit takes — and committed. */
  pickValue(value: string): void {
    const target = this.pickerTarget
    this.picker.close()
    this.pickerTarget = null
    if (!target) return
    this.onEdit(target.row, target.field, value)
    this.onCellCommit(target.row, target.field)
  }

  // ── Dates ────────────────────────────────────────────────────────────────────
  // A date cell is the app's `nf-datepicker`: the control the dialog behind this and the bulk grid
  // already use, and the only thing here with a calendar. It is a ControlValueAccessor with no value
  // input — its inner input binds `[formControl]` to the control it is given — so the control is the
  // whole channel. It holds the ISO form the picker reads and writes; the line keeps what its column
  // DISPLAYS (`dateUS`: `MM/dd/yyyy`), like every other cell here, which is what Done hands back.

  /** The control a date cell binds to, created on first ask and the same one thereafter. */
  dateControl(
    row: CollectionItemRow,
    column: EditTransactionCollectionColumn,
  ): FormControl {
    let byColumn = this.dateControls.get(row)
    if (!byColumn) {
      byColumn = new Map<string, FormControl>()
      this.dateControls.set(row, byColumn)
    }
    const existing = byColumn.get(column.field)
    if (existing) return existing

    // The ISO form, not the displayed one: `nf-datepicker` parses the model and formats it itself.
    const control = new FormControl(editValue(row[column.field], column.style))
    this.dateBindings.add(
      control.valueChanges.subscribe((iso: string | null) => {
        // A pick, or a typed date the control could read, is committed on the spot — as an enum's pick
        // is: the control owns its own display, so there is no edit to wait for a blur on. Picking the
        // day the cell already holds is not an edit, so it leaves the line (and `dirty`) alone.
        const display = formatFieldValue(iso ?? '', column.style)
        if (display === String(row[column.field] ?? '')) return
        this.onEdit(row, column.field, display)
        this.onCellCommit(row, column.field)
      }),
    )
    byColumn.set(column.field, control)
    return control
  }

  /**
   * Seeds the working copy from the projected lines. Called by the host after `collection` is set.
   *
   * `accountId` is the account the dialog behind this was opened for: every `/resolve` call this
   * modal makes — a search, a per-line derivation — names it.
   */
  init(
    collection: EditTransactionCollection,
    context: { accountId: string } = { accountId: '' },
  ): void {
    this.collection = collection
    this.accountId = context.accountId
    this.triggers = derivationTriggers(collection.derivations ?? [])
    this.rows = collection.rows.map((line, position) => {
      const row: CollectionItemRow = {}
      collection.columns.forEach((column, index) => {
        row[column.field] = line[index] ?? ''
      })
      // Which original line this is — absent for one the dialog added before reopening here.
      const origin = collection.lineOrigins
        ? collection.lineOrigins[position]
        : position
      if (origin != null) row[LINE_ORIGIN_KEY] = String(origin)
      this.committed.set(row, { ...row })
      return row
    })
    this.dirty = false
    this.sizeToColumns()
  }

  // ── Per-line derivation (PVAL-26685) ────────────────────────────────────────────────────────
  // A column whose inputs read a sibling on its OWN line (`scope: "row"`) is derived per line: a
  // distribution line's sub-type and currency follow that line's account and no other. Keyed by
  // the line object rather than its index, so removing a line above cannot misroute a reply.

  /**
   * A cell's edit, committed — a pick, or the box losing focus. Re-derives that line's dependants
   * when the value actually changed.
   */
  onCellCommit(row: CollectionItemRow, field: string): void {
    const last = this.committed.get(row) ?? {}
    const now = String(row[field] ?? '').trim()
    if (String(last[field] ?? '').trim() === now) return
    this.committed.set(row, { ...last, [field]: row[field] })
    this.deriveLine(row, field, new Set())
  }

  private deriveLine(
    row: CollectionItemRow,
    changed: string,
    seen: Set<string>,
  ): void {
    const dependents = this.triggers.get(changed)
    if (!dependents?.length) return
    const pending = (this.collection?.derivations ?? []).filter(
      entry => dependents.includes(entry.field) && !seen.has(entry.field),
    )
    pending.forEach(entry => seen.add(entry.field))
    this.runLineDerivations(row, pending, seen)
  }

  /** One `/resolve` call for every column of this line the commit made stale. */
  private runLineDerivations(
    row: CollectionItemRow,
    derivations: readonly FieldDerivation[],
    seen: Set<string>,
  ): void {
    const stamps = this.derivationSeq.get(row) ?? new Map<string, number>()
    this.derivationSeq.set(row, stamps)

    const seqs = new Map<string, number>()
    const items = derivations.flatMap(derivation => {
      const seq = ++this.nextSeq
      stamps.set(derivation.field, seq)
      const request = derivationRequest(
        derivation,
        // A row-scoped input reads THIS line's cell; the line is keyed by the full column field.
        name => row[name],
        () => undefined,
      )
      if (!request) return []
      seqs.set(request.field, seq)
      return [{ keyName: request.keyName, params: request.params }]
    })
    if (!items.length) return

    this.derivationCalls.add(
      this.derivations
        .resolve(items, this.accountId)
        .subscribe(results => this.applyLine(row, results, seqs, seen)),
    )
  }

  private applyLine(
    row: CollectionItemRow,
    results: readonly DerivationResult[],
    seqs: ReadonlyMap<string, number>,
    seen: Set<string>,
  ): void {
    const stamps = this.derivationSeq.get(row)
    const byKey = new Map(results.map(result => [result.keyName, result]))
    seqs.forEach((seq, field) => {
      if (stamps?.get(field) !== seq) return
      const result = byKey.get(field)
      if (!result || result.error != null) {
        console.error(
          `[transactions-2] could not derive ${field} on this line; the cell keeps its value`,
          result?.error ?? 'no result',
        )
        return
      }
      if (result.value == null) return
      row[field] = String(result.value)
      this.committed.set(row, {
        ...(this.committed.get(row) ?? {}),
        [field]: row[field],
      })
      this.dirty = true
      this.deriveLine(row, field, seen)
    })
  }

  /** Which control a column edits with. Read from metadata, never from the column's name. */
  control(column: ControlSource): EditTransactionControl {
    return controlFor(column)
  }

  /**
   * How wide a column renders: its control's seed width, stretched by {@link fill} when the whole
   * collection is narrower than {@link MIN_MODAL_WIDTH} so the extra room goes to the columns.
   */
  width(column: ControlSource): number {
    return Math.round(this.seedWidth(column) * this.fill)
  }

  /** A column's width before any stretching — the prototype's own seed for its control. */
  private seedWidth(column: ControlSource): number {
    return ITEM_WIDTH[controlFor(column)]
  }

  /** Everything on a line that is not a column: gutters, gaps, body padding and the scrollbar. */
  private get chrome(): number {
    const columns = this.collection?.columns.length ?? 0
    // One gap between every pair of the line's children, which are the columns plus two gutters.
    return (
      BODY_PADDING +
      LINE_GUTTERS +
      LINE_GAP * (columns + 1) +
      SCROLLBAR_ALLOWANCE
    )
  }

  /** The columns' seed widths added up. */
  private get seedCells(): number {
    return (this.collection?.columns ?? []).reduce(
      (total, column) => total + this.seedWidth(column),
      0,
    )
  }

  /**
   * The factor the columns stretch by so a short collection fills {@link MIN_MODAL_WIDTH}. Exactly 1
   * for one already wider than that — Distribution Lines is 940 by its own columns — so a wide
   * collection renders as it always did.
   */
  private get fill(): number {
    const cells = this.seedCells
    return cells > 0 ? Math.max(1, (MIN_MODAL_WIDTH - this.chrome) / cells) : 1
  }

  /**
   * The collection's own actions are withheld: no line added, none removed, nothing to apply.
   *
   * The summary's `edit: false`, not any column's. The cells inside a line answer for their own
   * flag, so a locked collection can still hold an editable column and the two never stand in
   * for one another.
   */
  get locked(): boolean {
    return this.collection?.editable === false
  }

  /** Whether any column adds, and there is a line to add — the Σ row's only precondition. */
  get hasTotals(): boolean {
    return (
      this.rows.length > 0 &&
      (this.collection?.columns.some(column => column.sums) ?? false)
    )
  }

  /**
   * The width one line needs, gutters and gaps included — or {@link MIN_MODAL_WIDTH}, whichever is
   * larger. Built from the SEED widths, not {@link width}: the stretched widths are derived from
   * this floor, so reading them back here would be circular.
   */
  get modalWidth(): number {
    return Math.max(MIN_MODAL_WIDTH, this.chrome + this.seedCells)
  }

  /**
   * Sizes the dialog to the collection that is OPEN, rather than to the widest one that exists.
   *
   * Done from {@link init} because that is the first moment the columns are known: ng-bootstrap
   * has already created and rendered the component by the time the host assigns them, so
   * `ngAfterViewInit` has run and has nothing to measure.
   *
   * Reached with `closest`, because the element to size is ng-bootstrap's rather than this
   * component's. That is not a new coupling: the template already names `.modal-dialog`, to
   * drag it by.
   */
  private sizeToColumns(): void {
    const dialog = this.host.nativeElement.closest<HTMLElement>('.modal-dialog')
    if (dialog) dialog.style.width = `${this.modalWidth}px`
  }

  onEdit(row: CollectionItemRow, field: string, value: string): void {
    row[field] = value
    this.dirty = true
  }

  /** A new line starts empty rather than cloning the last one: copying a line silently duplicates
   * an amount, which is the one mistake a journal entry must not make quietly. */
  addRow(): void {
    const row: CollectionItemRow = {}
    this.collection.columns.forEach(column => {
      row[column.field] = ''
    })
    this.rows = [...this.rows, row]
    this.dirty = true
  }

  removeRow(index: number): void {
    this.rows = this.rows.filter((_, position) => position !== index)
    this.dirty = true
  }

  /**
   * The Σ row: a total per column that adds, blank for every column that does not.
   *
   * Returned as text so a column with nothing to add renders empty rather than `0`, which would
   * claim a total the metadata never asked for.
   */
  total(column: EditTransactionCollectionColumn): string {
    if (!column.sums) return ''
    const sum = this.rows.reduce((running, row) => {
      const numeric = Number(String(row[column.field] ?? '').replace(/,/g, ''))
      return running + (Number.isFinite(numeric) ? numeric : 0)
    }, 0)
    return sum.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
  }

  /**
   * How many required cells are still empty, across every line.
   *
   * Reported rather than enforced, like the dialog's own note: Phase 1 has no validation call, so
   * Done stays live and the footer says what a real save would reject.
   *
   * A column whose list is declared but unresolvable is skipped — it renders disabled, so counting its
   * blank would point the note at a cell nobody can fill. Mirrors `collectionRequiredUnsatisfied`,
   * which the dialog's own footer reads (PVAL-26493).
   */
  get missingRequiredCount(): number {
    const required =
      this.collection?.columns.filter(c => c.required && !c.listPending) ?? []
    return this.rows.reduce(
      (count, row) =>
        count +
        required.filter(column => !String(row[column.field] ?? '').trim())
          .length,
      0,
    )
  }

  apply(): void {
    if (this.locked) return
    this.activeModal.close({
      name: this.collection.name,
      rows: this.rows,
    } as CollectionItemsResult)
  }
}
